import classnames from "classnames";
import * as React from "react";

import { UseFileUploadParams, useFileUpload } from "@shared/hooks";
import { FileList } from "@shared/services/fileUpload";
import Message from "@shared/services/message";
import { formatBytes } from "@shared/services/string";

import Banner from "../banner/Banner";
import FileCard from "../fileCard/FileCard";
import { Icon } from "../icon";
import Loading from "../loading/Loading";
import Modal, { ModalProps } from "../modal/Modal";

interface FileUploadModalProps
  extends Pick<ModalProps, "onClickCancel">,
    UseFileUploadParams {
  onUpload?: (value: FileList[]) => void;
  onDirty?: (value: boolean) => void;
  onClickCancel?: () => void;
}

export const FileUploadModal = ({
  initialFileList = [],
  initialRemainingSize = 0,
  initialRemainingLimit = 0,
  onUpload,
  onDirty,
  onClickCancel,
}: FileUploadModalProps) => {
  const dropzoneStyle = classnames("code-c-file-upload-modal__dropzone", {});

  const {
    fileCount,
    fileList,
    isReadingFiles,
    modifiedZip,
    rejectFiles,
    remainingSize,
    getInputProps,
    getRootProps,
    removeFile,
  } = useFileUpload({
    initialFileList,
    initialRemainingSize,
    initialRemainingLimit,
  });
  const formatRemainingSize = formatBytes(remainingSize);
  const isDirty = fileList.length > 0;
  const label = {
    title: Message.getMessageByKey("uploadFilesModal.title"),
    description: Message.getMessageByKey("uploadFilesModal.description"),
    ok: Message.getMessageByKey("common.upload"),
    browse: Message.getMessageByKey("uploadFilesModal.browse"),
    dragdrop: Message.getMessageByKey("uploadFilesModal.dragdrop"),
    size: Message.getMessageByKey("uploadFilesModal.size").replace(
      "{0}",
      formatBytes(initialRemainingSize),
    ),
    total: Message.getMessageByKey("uploadFilesModal.total").replace(
      "{0}",
      `${initialRemainingLimit}`,
    ),
    remainingSize: Message.getMessageByKey(
      "uploadFilesModal.remainingSize",
    ).replace("{0}", formatRemainingSize),
    remainingTotal: Message.getMessageByKey(
      "uploadFilesModal.remainingTotal",
    ).replace("{0}", `${fileCount}`),
    errorTitle: Message.getMessageByKey("uploadFilesModal.errorTitle"),
    modifiedTitle: Message.getMessageByKey("uploadFilesModal.modifiedTitle"),
    navigationConfirm: Message.getMessageByKey(
      "navigation.confirm.uploadFiles",
    ),
  };

  const handleRemove = (index: number) => () => {
    removeFile(index);
  };

  const handleSubmit = async () => {
    onUpload?.(fileList);
  };

  const handleClose = () => {
    if (isReadingFiles) {
      return;
    }
    onClickCancel?.();
  };

  React.useEffect(() => {
    onDirty?.(isDirty);
  }, [isDirty, onDirty]);

  return (
    <Modal
      isOpen
      title={label.title}
      className="code-c-file-upload-modal"
      contentClassName="code-c-file-upload-modal__content"
      okButtonLabel={label.ok}
      disableCancel={isReadingFiles}
      disableClose={isReadingFiles}
      disableOk={!isDirty || isReadingFiles}
      onClickBackground={handleClose}
      onClickCancel={handleClose}
      onClickOk={handleSubmit}
      onClose={handleClose}
    >
      <Loading isOpen={isReadingFiles} fullScreen={false} overlay={false} />
      <p className="code-c-file-upload-modal__description">
        {label.description}
      </p>
      <div
        {...getRootProps({
          className: dropzoneStyle,
        })}
      >
        <input {...getInputProps()} />
        <Icon
          type="cloud-arrow-up"
          size="3xl"
          className="code-c-file-upload-modal__drop-icon"
        />
        <div>
          <p className="code-c-file-upload-modal__browse-label text-xl">
            {label.browse}
          </p>
          <p className="code-c-file-upload-modal__drop-label">
            {label.dragdrop}
          </p>
        </div>
        <div className="code-c-file-upload-modal__file-rules text-xs">
          <p>
            {label.size}
            &nbsp;
            {remainingSize !== initialRemainingSize && label.remainingSize}
          </p>
          <p>
            {label.total}
            &nbsp;
            {fileCount !== initialRemainingLimit && label.remainingTotal}
          </p>
        </div>
      </div>
      {Boolean(modifiedZip.length) && (
        <Banner type="warning">
          <div className="code-c-file-upload-modal__files-warning">
            <h4>{label.modifiedTitle}</h4>
            <ul className="code-c-file-upload-modal__files-warning-list">
              {modifiedZip.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        </Banner>
      )}
      {rejectFiles.length > 0 && (
        <Banner type="error">
          <div className="code-c-file-upload-modal__files-error">
            <p>
              <b>{label.errorTitle}</b>
            </p>
            <ul className="code-c-file-upload-modal__files-error-list">
              {rejectFiles.map(({ name, message }) => (
                <li key={name}>{message}</li>
              ))}
            </ul>
          </div>
        </Banner>
      )}
      {fileList.length > 0 && (
        <div className="code-c-file-upload-modal__files">
          {fileList.map(({ file, formatSize }, index) => (
            <FileCard
              key={file.name}
              fileName={file.name}
              fileSize={formatSize}
              isRemove
              onClick={handleRemove(index)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default FileUploadModal;
